<template>
  <div class="account_usage">
    <Loading v-if="!account" />
    <div v-if="account">
      <v-layout column align-center>
        <v-container fluid>
          <v-row class="align-center">
            <v-col class="text-right">
              <span class="text-lg-h6"> {{ account.name }} &nbsp;&nbsp; </span>
            </v-col>
            <v-col class="text-left">
              <account-actions
                class="d-flex"
                :account="account"
                show_details
                show_orders
                show_users
              ></account-actions>
            </v-col>
          </v-row>
          <hr />
          <br />
          <usage-table
            :usage="account_usage"
            :tasking_usage="tasking_usage"
            :users_usage="users_usage"
            :usage_date="response_date"
            :start_date="start_date"
            :end_date="end_date"
            :loading="loading"
            @usage_range_changed="refresh_usage($event.start, $event.end)"
            @view_orders="view_orders($event.start_date, $event.end_date)"
          >
          </usage-table>
        </v-container>
      </v-layout>
      <confirm-modal
        ref="confirmResetModal"
        title=""
        confirm-btn-text="Email report"
        cancel-btn-text="Narrow date range"
        cancel-btn-class="v-btn--flat v-btn--outlined outlined--text"
        confirm-btn-class="black--text ml-4"
      >
        <div style="max-width: 350px">
          This request is too large to process. Try narrowing the date range or
          request to have a usage report emailed to {{ user_details.email }}.
        </div>
      </confirm-modal>
    </div>
  </div>
</template>
<script>
import adminApi from "@/api/admin";
import usageApi from "@/api/usage";
import store from "@/store/index";
import UsageTable from "@/components/usage/UsageTable.vue";
import AccountActions from "@/components/account/AccountActions.vue";
import ConfirmModal from "@/components/shared/ConfirmModal";
import { mapGetters } from "vuex";
import utils from "@/utils/userType";
import Loading from "@/components/shared/Loading.vue";
import { createNamespacedHelpers } from "vuex-composition-helpers";
const uiStore = createNamespacedHelpers("ui");

export default {
  name: "AccountUsage",
  components: {
    UsageTable,
    AccountActions,
    Loading,
    ConfirmModal,
  },
  data: function () {
    return {
      user_info: store.state.user,
      loading: false,
      account_usage: null,
      tasking_usage: null,
      users_usage: [],
      account: null,
      response_date: null,
      start_date: this.$route.query.start_date || null,
      end_date: this.$route.query.end_date || null,
    };
  },
  mounted() {
    return adminApi
      .get_account(this.$route.params.account_id)
      .then((response) => {
        this.account = response.data.account;
        this.refresh_usage();
      })
      .catch((error) => {
        this.account_usage = {};
        return error;
      });
  },
  setup() {
    const { SHOW_SNACKBAR } = uiStore.useMutations(["SHOW_SNACKBAR"]);
    return { SHOW_SNACKBAR };
  },
  watch: {
    $route() {
      this.refresh_usage();
    },
  },
  computed: {
    ...mapGetters(["user_details", "account_details"]),
    is_admin_or_support() {
      return utils.is_admin_or_support(this.user_details);
    },
  },
  methods: {
    hasValidDates() {
      if (this.$route.query.start_date && this.$route.query.end_date) {
        const startDate = new Date(this.$route.query.start_date).getTime();
        const endDate = new Date(this.$route.query.end_date).getTime();

        return startDate <= endDate || endDate >= startDate;
      }

      return false;
    },
    async refresh_usage() {
      if (this.hasValidDates()) {
        this.loading = true;
        const start_date = this.$route.query.start_date || null;
        const end_date = this.$route.query.end_date || null;
        let query_string = "";
        if (start_date) {
          query_string += `start_date=${start_date}`;
        }
        if (end_date) {
          if (query_string) {
            query_string += "&";
          }
          query_string += `end_date=${end_date}`;
        }

        try {
          const response = await usageApi.get_account_usage(
            this.$route.params.account_id,
            query_string
          );
          this.response_date = response.data.response_timestamp;
          this.account_usage = response.data.usage;
          this.tasking_usage = response.data.tasking_usage;
          this.users_usage = this.flatten_users_usage(response.data.users);
          this.account_id = response.data.account_id;
        } catch (err) {
          this.account_usage = {};
          if (err.response.status === 504) {
            this.$refs.confirmResetModal.show().then(async (yes) => {
              if (yes) {
                let request = {
                  notifications: [
                    {
                      type: "email",
                      address: this.user_details.email,
                    },
                  ],
                };
                try {
                  const async_response =
                    await usageApi.post_async_account_usage(
                      this.$route.params.account_id,
                      query_string,
                      request
                    );
                  this.SHOW_SNACKBAR({
                    text: `Usage report will be sent to ${this.user_details.email} when it's ready.`,
                    color: "success",
                  });
                } catch (err) {
                  this.account_usage = {};
                }
              }
            });
          }
        } finally {
          this.loading = false;
        }
      } else {
        this.account_usage = null;
        this.users_usage = [];
      }
    },

    view_orders(start_date, end_date) {
      if (this.is_admin_or_support) {
        const account_id = this.$route.params.account_id;

        this.$router.push({
          name: "account-orders",
          params: { account_id: account_id },
          query: {
            start_date: start_date,
            end_date: end_date,
          },
        });
      } else {
        this.$router.push({
          name: "my-orders",
          params: { account_id: this.user_details.account_id },
          query: {
            start_date: start_date,
            end_date: end_date,
            filters: [`user_id:${this.user_details.user_id}`],
          },
        });
      }
    },

    flatten_users_usage(users_usage) {
      let flattened_usage = [];
      for (let i = 0; i < users_usage.length; i++) {
        flattened_usage.push({
          account_id: this.$route.params.account_id,
          user_id: users_usage[i].id,
          name: users_usage[i].name,
          fresh_cost: users_usage[i].cost.fresh_imagery_cost,
          standard_cost: users_usage[i].cost.standard_imagery_cost,
          training_cost: users_usage[i].cost.training_imagery_cost,
          total_cost: users_usage[i].cost.total_imagery_cost,
        });
      }
      return flattened_usage;
    },
  },
};
</script>
<style></style>
